import React, { useState } from 'react';
import { MapPin, Clock, Phone, Navigation, Fuel, Car, Wifi, Coffee, Users, Building, TrendingUp, CheckCircle, Handshake, Award, Target, Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const stationsData = {
  title: "Réseau de Stations Wayne",
  subtitle: "50+ Stations à Votre Service Partout en Côte d'Ivoire",
  regions: [
    {
      name: "Abidjan & Environs",
      count: 15,
      stations: [
        { name: "Wayne Plateau", address: "Avenue Franchet d'Espérey, Plateau", services: ["24/7", "Lavage", "Wi-Fi"] },
        { name: "Wayne Cocody Angré", address: "Boulevard des Martyrs, Cocody", services: ["24/7", "Mini-Market", "Vidange"] },
        { name: "Wayne Marcory", address: "Zone 4, Marcory", services: ["24/7", "Lavage", "Restauration"] },
      ]
    },
    {
      name: "Axes Autoroutiers",
      count: 20,
      stations: [
        { name: "Wayne Autoroute Nord", address: "Sortie Anyama, Autoroute du Nord", services: ["24/7", "Aire de repos", "Wi-Fi"] },
        { name: "Wayne Grand-Bassam", address: "Route de Grand-Bassam", services: ["24/7", "Lavage", "Mini-Market"] },
        { name: "Wayne Dabou", address: "Autoroute Abidjan-Dabou", services: ["24/7", "Restauration"] },
      ]
    },
    {
      name: "Villes Secondaires",
      count: 15,
      stations: [
        { name: "Wayne Yamoussoukro", address: "Avenue Houphouët-Boigny", services: ["24/7", "Lavage", "Vidange"] },
        { name: "Wayne Bouaké", address: "Quartier Commerce, Bouaké", services: ["24/7", "Mini-Market"] },
        { name: "Wayne San-Pédro", address: "Zone Portuaire, San-Pédro", services: ["24/7", "Restauration", "Wi-Fi"] },
      ]
    }
  ]
};

const partnershipData = {
  title: "Ouvrez Votre Station-Service Wayne",
  subtitle: "Modèle de Partenariat Rentable et Sécurisé",
  concept: "Devenez gérant d'une station-service sous la marque internationale Wayne, avec le support complet de Kip Energy pour assurer votre réussite.",
  options: [
    {
      icon: Fuel,
      title: "Devenez Partenaire Gérant",
      description: "Gérez votre propre station-service Wayne avec notre accompagnement technique et commercial complet."
    },
    {
      icon: Award,
      title: "Réseau Distribution Lubrifiants",
      description: "Rejoignez notre réseau de distribution de lubrifiants et consommables automobiles Wayne."
    }
  ],
  profileRequirements: [
    { icon: Users, text: "Entrepreneur dynamique et rigoureux" },
    { icon: TrendingUp, text: "Expérience commerce ou gestion (atout)" },
    { icon: Building, text: "Capacité financière (apport personnel min 30%)" },
    { icon: MapPin, text: "Terrain bien situé (axe passant, visibilité)" },
    { icon: Calendar, text: "Engagement long terme (min 10 ans)" },
    { icon: Target, text: "Disponibilité gestion quotidienne" }
  ],
  support: [
    "Assistance technique complète",
    "Approvisionnement produits garanti",
    "Marketing & Communication",
    "Système informatique intégré",
    "Formation initiale et continue"
  ]
};

const serviceIcons = {
  "24/7": Clock,
  "Lavage": Car,
  "Wi-Fi": Wifi,
  "Mini-Market": Coffee,
  "Vidange": Fuel,
  "Restauration": Coffee,
  "Aire de repos": Coffee,
};

const StationsSection = () => {
  const [activeTab, setActiveTab] = useState('reseau');

  return (
    <section id="stations" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">Notre Réseau</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {stationsData.title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {stationsData.subtitle}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-xl p-1.5 shadow-sm border border-slate-200">
            <button
              onClick={() => setActiveTab('reseau')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'reseau'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'text-slate-600 hover:text-orange-500'
              }`}
            >
              <MapPin className="w-4 h-4 inline mr-2" />
              Nos Stations
            </button>
            <button
              onClick={() => setActiveTab('partenaire')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'partenaire'
                  ? 'bg-orange-500 text-white shadow-md'
                  : 'text-slate-600 hover:text-orange-500'
              }`}
            >
              <Handshake className="w-4 h-4 inline mr-2" />
              Devenir Partenaire
            </button>
          </div>
        </div>

        {/* Réseau Tab Content */}
        {activeTab === 'reseau' && (
          <div className="animate-in slide-in-from-bottom-2">
            {/* Map Visual & Stats */}
            <div className="grid lg:grid-cols-2 gap-12 mb-16">
              {/* Map placeholder with Wayne image */}
              <div className="relative rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_5cce6493-f0af-4302-8a9d-51bbe4853e8f/artifacts/rxparwd0_PHOTO-2025-10-10-21-03-55%209.jpg"
                  alt="Station Wayne"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                
                {/* Overlay content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm mb-1">Couverture Nationale</p>
                      <p className="text-white text-2xl font-bold">Côte d'Ivoire</p>
                    </div>
                    <Button
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      asChild
                    >
                      <a href="#contact">
                        <Navigation className="w-4 h-4 mr-2" />
                        Itinéraire
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Floating stat cards */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                  <p className="text-2xl font-bold text-orange-500">50+</p>
                  <p className="text-xs text-slate-600">Stations</p>
                </div>
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg">
                  <p className="text-2xl font-bold text-orange-500">24/7</p>
                  <p className="text-xs text-slate-600">Service</p>
                </div>
              </div>

              {/* Region stats */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Nos Stations par Région</h3>
                
                {stationsData.regions.map((region, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:border-orange-200 transition-all"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{region.name}</h4>
                          <p className="text-sm text-slate-500">{region.count} stations</p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-orange-500">{region.count}</div>
                    </div>
                    
                    {/* Sample stations */}
                    <div className="space-y-2">
                      {region.stations.slice(0, 2).map((station, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{station.name}</span>
                          <div className="flex gap-1">
                            {station.services.slice(0, 2).map((service, sIdx) => {
                              const Icon = serviceIcons[service] || Clock;
                              return (
                                <div
                                  key={sIdx}
                                  className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center"
                                  title={service}
                                >
                                  <Icon className="w-3 h-3 text-slate-500" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Available */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Services Disponibles</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                  { icon: Fuel, label: "Carburants Premium", desc: "24/7" },
                  { icon: Car, label: "Lavage Auto", desc: "Express" },
                  { icon: Wifi, label: "Wi-Fi Gratuit", desc: "Haut débit" },
                  { icon: Coffee, label: "Mini-Market", desc: "Snacks & Boissons" },
                  { icon: Clock, label: "Service 24/7", desc: "Non-stop" },
                  { icon: Phone, label: "Assistance", desc: "Urgences" },
                ].map((service, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center p-4 rounded-xl hover:bg-orange-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
                      <service.icon className="w-6 h-6 text-orange-500" />
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{service.label}</p>
                    <p className="text-xs text-slate-500">{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Partenaire Tab Content */}
        {activeTab === 'partenaire' && (
          <div className="animate-in slide-in-from-bottom-2">
            {/* Hero Partnership Section */}
            <div className="grid lg:grid-cols-2 gap-12 mb-16">
              {/* Left - Image & CTA */}
              <div className="relative rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_5cce6493-f0af-4302-8a9d-51bbe4853e8f/artifacts/ituqbtbi_PHOTO-2025-10-10-21-03-55%202.jpg"
                  alt="Station Wayne Partnership"
                  className="w-full h-full min-h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                
                {/* Overlay content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-2">{partnershipData.title}</h3>
                  <p className="text-orange-300 text-lg mb-4">{partnershipData.subtitle}</p>
                  <Button
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    asChild
                  >
                    <a href="#contact">
                      <Handshake className="w-5 h-5 mr-2" />
                      Candidater Maintenant
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right - Partnership Options */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-500" />
                    Devenez Partenaire du Réseau Wayne
                  </h4>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {partnershipData.concept}
                  </p>
                </div>

                {/* Partnership Options */}
                {partnershipData.options.map((option, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-orange-200 hover:shadow-md transition-all group"
                  >
                    <div className="flex gap-4">
                      <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors">
                        <option.icon className="w-7 h-7 text-orange-500 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h5 className="font-bold text-slate-900 mb-2">{option.title}</h5>
                        <p className="text-slate-600 text-sm">{option.description}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* CTA Button */}
                <Button
                  variant="outline"
                  className="w-full border-orange-500 text-orange-500 hover:bg-orange-50"
                  size="lg"
                  asChild
                >
                  <a href="#contact">
                    Écrivez-nous pour plus d'informations
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Profil Partenaire Idéal */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 mb-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Profil Partenaire Idéal</h3>
                <p className="text-slate-600">Les critères pour rejoindre le réseau Wayne</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {partnershipData.profileRequirements.map((req, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 hover:bg-orange-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                      <req.icon className="w-5 h-5 text-orange-500" />
                    </div>
                    <p className="text-slate-700 font-medium">{req.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Support Kip Energy */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Support Kip Energy</h3>
                <p className="text-white/80 mb-6">
                  En tant que partenaire, vous bénéficiez d'un accompagnement complet pour assurer votre succès.
                </p>
                <ul className="space-y-3">
                  {partnershipData.support.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-white/90 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-3xl p-8 border border-slate-100">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Processus de Candidature</h3>
                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="step1" className="border rounded-xl px-4">
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 font-bold text-sm">1</span>
                        Pré-Qualification
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pl-11">
                      Soumettez votre dossier de candidature avec vos informations personnelles et votre projet.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="step2" className="border rounded-xl px-4">
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 font-bold text-sm">2</span>
                        Étude de Faisabilité
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pl-11">
                      Notre équipe analyse votre emplacement et valide le potentiel commercial du projet.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="step3" className="border rounded-xl px-4">
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 font-bold text-sm">3</span>
                        Validation & Signature
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pl-11">
                      Signature du contrat de partenariat et finalisation des modalités financières.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="step4" className="border rounded-xl px-4">
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 font-bold text-sm">4</span>
                        Construction & Formation
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pl-11">
                      Accompagnement dans la construction de votre station et formation complète de votre équipe.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="step5" className="border rounded-xl px-4">
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 font-bold text-sm">5</span>
                        Ouverture
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 pl-11">
                      Inauguration de votre station Wayne et démarrage de l'activité avec notre support continu.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>

            {/* Final CTA */}
            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-4">Prêt à rejoindre le réseau Wayne ?</p>
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                asChild
              >
                <a href="#contact">
                  <Handshake className="w-5 h-5 mr-2" />
                  Contactez-Nous
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default StationsSection;
